import React, { useEffect, useState } from "react";
import {
  addTestMark,
  deleteTestMark,
  getClasses,
  getStudents,
  getTestMarkSubjects,
  getTestMarks,
  updateTestMark
} from "../services/api";
import DateInput from "../components/DateInput";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import { formatDate, toInputDate } from "../utils/helpers";
import "./TestMarksheetPage.css";

const today = () => new Date().toISOString().split("T")[0];

const createFormState = () => ({
  studentId: "",
  testName: "",
  subject: "",
  marks: "",
  maxMarks: "100",
  testDate: today(),
  remarks: ""
});

function TestMarksheetPage() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState("all");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [formState, setFormState] = useState(createFormState());

  const loadStudentsAndClasses = async () => {
    const [studentsRes, classesRes, subjectsRes] = await Promise.all([
      getStudents("all", { fields: "lookup" }),
      getClasses(),
      getTestMarkSubjects()
    ]);
    setStudents(studentsRes.data);
    setClasses(classesRes.data);
    setSubjects(subjectsRes.data);
  };

  const loadMarks = async (classFilter = selectedClass) => {
    try {
      setLoading(true);
      const response = await getTestMarks({ class: classFilter });
      setRecords(response.data);
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to load test marks",
        message: error.response?.data?.error || "Test mark records could not be loaded."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        await loadStudentsAndClasses();
        await loadMarks("all");
      } catch (error) {
        showAppToast({
          type: "error",
          title: "Failed to initialize marksheet",
          message: error.response?.data?.error || "Initial marksheet data could not be loaded."
        });
        setLoading(false);
      }
    };

    initialize();
  }, []);

  const filteredStudents = students.filter((student) =>
    selectedClass === "all" ? true : student.class === selectedClass
  );

  const handleFormChange = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setEditingId("");
    setFormState(createFormState());
  };

  const handleClassChange = async (value) => {
    setSelectedClass(value);
    if (value !== "all" && !filteredStudents.some((student) => student._id === formState.studentId)) {
      setFormState((current) => ({ ...current, studentId: "" }));
    }
    await loadMarks(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setWorkingId(editingId || "create");
      if (editingId) {
        const response = await updateTestMark(editingId, formState);
        setRecords((current) =>
          current.map((item) => (item._id === editingId ? response.data.record : item))
        );
        showAppToast({
          type: "success",
          title: "Test marks updated",
          message: "The test record has been updated successfully."
        });
      } else {
        const response = await addTestMark(formState);
        setRecords((current) => [response.data.record, ...current]);
        const whatsappReason = response.data.whatsapp?.ok
          ? "Parent WhatsApp message sent successfully."
          : response.data.whatsapp?.reason || "Parent WhatsApp message could not be sent.";
        showAppToast({
          type: response.data.whatsapp?.ok ? "success" : "warning",
          title: "Test marks saved",
          message: whatsappReason
        });
        if (response.data.record?.subject && !subjects.includes(response.data.record.subject)) {
          setSubjects((current) => [...current, response.data.record.subject].sort());
        }
      }
      resetForm();
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to save test marks",
        message: error.response?.data?.error || "The test record could not be saved."
      });
    } finally {
      setWorkingId("");
    }
  };

  const handleEdit = (record) => {
    setEditingId(record._id);
    setFormState({
      studentId: record.student_id,
      testName: record.testName,
      subject: record.subject,
      marks: String(record.marks),
      maxMarks: String(record.maxMarks),
      testDate: toInputDate(record.testDate),
      remarks: record.remarks || ""
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (record) => {
    const confirmed = await confirmAppAction({
      title: "Delete test record",
      message: `This will remove the ${record.subject} result for ${record.student_name}.`,
      confirmLabel: "Delete",
      type: "danger"
    });

    if (!confirmed) {
      return;
    }

    try {
      setWorkingId(record._id);
      await deleteTestMark(record._id);
      setRecords((current) => current.filter((item) => item._id !== record._id));
      if (editingId === record._id) {
        resetForm();
      }
      showAppToast({
        type: "success",
        title: "Test record deleted",
        message: "The marksheet entry has been removed."
      });
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to delete test record",
        message: error.response?.data?.error || "The marksheet entry could not be removed."
      });
    } finally {
      setWorkingId("");
    }
  };

  return (
    <section className="test-marksheet">
      <div className="test-marksheet__header">
        <div>
          <span className="test-marksheet__eyebrow">Dashboard Handle</span>
          <h1>Test / Marksheet</h1>
          <p>Save weekly subject-wise marks and automatically send the result to parent WhatsApp numbers.</p>
        </div>
      </div>

      <div className="test-marksheet__layout">
        <form className="test-marksheet__card test-marksheet__form" onSubmit={handleSubmit}>
          <div className="test-marksheet__card-head">
            <h2>{editingId ? "Edit Test Result" : "Add Test Result"}</h2>
            {editingId && (
              <button type="button" className="test-marksheet__ghost" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
          </div>

          <div className="test-marksheet__grid">
            <label>
              Class
              <select value={selectedClass} onChange={(e) => handleClassChange(e.target.value)}>
                <option value="all">All Classes</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </label>

            <label>
              Student
              <select
                value={formState.studentId}
                onChange={(e) => handleFormChange("studentId", e.target.value)}
                required
              >
                <option value="">Select Student</option>
                {filteredStudents.map((student) => (
                  <option key={student._id} value={student._id}>
                    {student.name} ({student.class})
                  </option>
                ))}
              </select>
            </label>

            <label>
              Test Name
              <input value={formState.testName} onChange={(e) => handleFormChange("testName", e.target.value)} required />
            </label>

            <label>
              Subject
              <input
                list="marksheet-subjects"
                value={formState.subject}
                onChange={(e) => handleFormChange("subject", e.target.value)}
                required
              />
              <datalist id="marksheet-subjects">
                {subjects.map((subject) => (
                  <option key={subject} value={subject} />
                ))}
              </datalist>
            </label>

            <label>
              Marks
              <input type="number" min="0" value={formState.marks} onChange={(e) => handleFormChange("marks", e.target.value)} required />
            </label>

            <label>
              Max Marks
              <input type="number" min="1" value={formState.maxMarks} onChange={(e) => handleFormChange("maxMarks", e.target.value)} required />
            </label>

            <label>
              Test Date
              <DateInput value={formState.testDate} onChange={(e) => handleFormChange("testDate", e.target.value)} required />
            </label>

            <label className="test-marksheet__grid-span-2">
              Remarks
              <textarea value={formState.remarks} onChange={(e) => handleFormChange("remarks", e.target.value)} rows="4" />
            </label>
          </div>

          <button className="test-marksheet__submit" type="submit" disabled={workingId === editingId || workingId === "create"}>
            {editingId ? "Update Test Result" : "Save And Send WhatsApp"}
          </button>
        </form>

        <div className="test-marksheet__card test-marksheet__list-card">
          <div className="test-marksheet__card-head">
            <h2>Saved Test Records</h2>
          </div>

          {loading ? (
            <div className="test-marksheet__empty">Loading test records...</div>
          ) : records.length === 0 ? (
            <div className="test-marksheet__empty">No test records have been added yet.</div>
          ) : (
            <div className="test-marksheet__table-wrap">
              <table className="test-marksheet__table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Test</th>
                    <th>Subject</th>
                    <th>Marks</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record._id}>
                      <td>{record.student_name}</td>
                      <td>{record.class}</td>
                      <td>{record.testName}</td>
                      <td>{record.subject}</td>
                      <td>{record.marks}/{record.maxMarks}</td>
                      <td>{formatDate(record.testDate)}</td>
                      <td>
                        <div className="test-marksheet__actions">
                          <button className="test-marksheet__action test-marksheet__action--edit" onClick={() => handleEdit(record)}>
                            Edit
                          </button>
                          <button
                            className="test-marksheet__action test-marksheet__action--delete"
                            onClick={() => handleDelete(record)}
                            disabled={workingId === record._id}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default TestMarksheetPage;
